import React from "react";
import { Box, Typography, Button, Grid } from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import MenuBookOutlinedIcon from "@mui/icons-material/MenuBookOutlined";
import styles from "./WorkspacesIntro.module.css";

const INTRO_PARAGRAPHS = [
  "Follow the RURACTIVE methodology to build a Rural Innovation Ecosystem (RIE) around the Rural Development Drivers relevant to your territory.",
  "No prior knowledge needed — you'll be guided through each step.",
];

const STEPS = [
  {
    step: 0,
    title: "Getting Started",
    lead: "Set the starting point for your RIE.",
    details:
      "Identify the RDDs relevant to your territory and define its scope, coordinator and sites.",
    bullets: [
      "Familiarise with the methodology",
      "Identify relevant RDDs",
      "Define your territory",
      "Set up the RIE structure",
    ],
    result: "A defined starting context for your RIE.",
  },
  {
    step: 1,
    title: "Stakeholder Identification",
    lead: "Find the stakeholders that matter for your RIE.",
    details:
      "Build a stakeholder database and analyse stakeholders by interest, influence and inclusion.",
    bullets: [
      "Identify stakeholders",
      "Analyse interest, influence & inclusion",
      "Prioritise stakeholders",
      "Start your Local Task Force (LTF)",
    ],
    result: "A prioritised stakeholder landscape and an initial LTF.",
  },
  {
    step: 2,
    title: "Stakeholder Engagement",
    lead: "Move from identifying stakeholders to involving them.",
    details:
      "Define your communication, enrol the Local Task Force and involve stakeholders.",
    bullets: [
      "Define messages and channels",
      "Establish the LTF",
      "Involve stakeholders",
    ],
    result: "An established LTF and a clear engagement approach.",
  },
  {
    step: 3,
    title: "Stakeholder Empowerment",
    lead: "Strengthen stakeholder involvement in the RIE.",
    details:
      "Work with stakeholders on common goals and pathways through collaborative activities.",
    bullets: ["Collaborate on shared goals", "Define pathways together"],
    result: "Stronger involvement and continued collaboration.",
  },
];

const WorkspacesIntro = ({ onStart }) => {
  return (
    <Box className={styles.root}>
      {/* Hero */}
      <Box className={`${styles.glass} ${styles.hero}`}>
        <Typography className={styles.eyebrow}>RURACTIVE WORKSPACE</Typography>
        <Typography variant="h3" className={styles.heroTitle}>
          Build your Rural Innovation Ecosystem
        </Typography>
        <Box className={styles.heroText}>
          {INTRO_PARAGRAPHS.map((paragraph) => (
            <Typography key={paragraph} variant="body1" className={styles.heroParagraph}>
              {paragraph}
            </Typography>
          ))}
        </Box>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={onStart}
          className={styles.heroButton}
        >
          Create Ruractive Project
        </Button>
      </Box>

      {/* The four steps */}
      <Box className={styles.section}>
        <Typography variant="h5" className={styles.sectionTitle}>
          The four steps
        </Typography>
        <Grid container spacing={2.5}>
          {STEPS.map((step) => (
            <Grid item xs={12} md={6} key={step.step}>
              <Box className={styles.stepCard}>
                <Box className={styles.stepBadge}>STEP {step.step}</Box>
                <Typography variant="h5" className={styles.stepTitle}>
                  {step.title}
                </Typography>
                <Typography className={styles.stepLead}>{step.lead}</Typography>
                <Typography className={styles.stepDetails}>
                  {step.details}
                </Typography>

                <Box className={styles.stepBullets}>
                  <Typography className={styles.stepBulletsLabel}>
                    In this step you will:
                  </Typography>
                  <ul className={styles.stepList}>
                    {step.bullets.map((bullet) => (
                      <li key={bullet}>
                        <span className={styles.stepDot} />
                        {bullet}
                      </li>
                    ))}
                  </ul>
                </Box>

                <Box className={styles.stepResult}>
                  <Typography className={styles.stepResultLabel}>
                    Main result
                  </Typography>
                  <Typography className={styles.stepResultText}>
                    {step.result}
                  </Typography>
                </Box>
              </Box>
            </Grid>
          ))}
        </Grid>
        <Box className={styles.methodologyRow}>
          <Button
            variant="outlined"
            startIcon={<MenuBookOutlinedIcon />}
            onClick={() =>
              window.open("/documents/HANDBOOK_Del4.1_4.pdf", "_blank")
            }
            className={styles.methodologyButton}
          >
            View methodology
          </Button>
        </Box>
      </Box>
    </Box>
  );
};

export default WorkspacesIntro;
